/**
 * Chart.js plugins for dashboard theming.
 *
 * These plugins can be added to a theme's chartjs.plugins array to customize
 * chart rendering behavior.
 */

import { Color } from "./color";

// ================================
// Color Extraction Helper
// ================================

/**
 * Extracts a hex color from various color formats (hex, rgba, rgb, hsl).
 * Returns null if the color cannot be extracted.
 */
function extractHexColor(color: unknown): string | null {
  if (typeof color !== "string") {
    return null;
  }

  const parsed = Color.tryParse(color);
  return parsed ? parsed.toHexString() : null;
}

// ================================
// Chart.js Plugins
// ================================

/**
 * Creates a Chart.js plugin that applies gradient backgrounds to bar charts.
 * The gradient runs along the value axis (from base to tip of each bar).
 */
export function createBarGradientPlugin(options: {
  startLuminosity: number; // Luminosity at base of bar
  endLuminosity: number; // Luminosity at tip of bar
  startOpacity: number; // Opacity at base of bar
  endOpacity: number; // Opacity at tip of bar
  gradientEndPosition: number; // Position (0-1) where gradient transition ends
}) {
  const startLum = options.startLuminosity;
  const endLum = options.endLuminosity;
  const startOpacity = options.startOpacity;
  const endOpacity = options.endOpacity;
  const endPosition = options.gradientEndPosition;

  return {
    id: "barGradient",
    // Use beforeInit to set up scriptable backgroundColor before Chart.js resolves colors
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    beforeInit(chart: any) {
      if (chart.config.type !== "bar") {
        return;
      }

      const isHorizontalBar = chart.config.options?.indexAxis === "y";

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      chart.data.datasets.forEach((dataset: any) => {
        // Skip if backgroundColor is already a function
        if (typeof dataset.backgroundColor === "function") {
          return;
        }

        const rawBackgroundColor = dataset.backgroundColor;
        // Pre-compute per-datapoint gradient stops so custom per-bar colors are preserved.
        const colorsByIndex = Array.isArray(rawBackgroundColor) ? rawBackgroundColor : null;
        const singleColorHex = colorsByIndex ? null : extractHexColor(rawBackgroundColor);
        if (!colorsByIndex && !singleColorHex) {
          return;
        }

        const gradientColorCache = new Map<number, { startColor: string; endColor: string }>();
        const getBaseColor = (dataIndex: number) => {
          if (!colorsByIndex || colorsByIndex.length === 0) {
            return rawBackgroundColor;
          }
          const clampedDataIndex = Math.min(Math.max(dataIndex, 0), colorsByIndex.length - 1);
          return colorsByIndex[clampedDataIndex];
        };

        const getGradientColors = (dataIndex: number) => {
          if (gradientColorCache.has(dataIndex)) {
            return gradientColorCache.get(dataIndex);
          }

          const baseColor = getBaseColor(dataIndex);
          const hexColor = extractHexColor(baseColor);
          if (!hexColor) {
            return null;
          }

          const baseColorObj = Color.parse(hexColor);
          const gradientColors = {
            startColor: baseColorObj.lighten(startLum).withAlpha(startOpacity).toRgbString(),
            endColor: baseColorObj.lighten(endLum).withAlpha(endOpacity).toRgbString(),
          };
          gradientColorCache.set(dataIndex, gradientColors);
          return gradientColors;
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dataset.backgroundColor = (context: any) => {
          const chartInstance = context.chart;
          const { ctx, chartArea } = chartInstance;
          const dataIndex = context.dataIndex ?? 0;
          const gradientColors = getGradientColors(dataIndex);
          if (!gradientColors) {
            return getBaseColor(dataIndex);
          }

          const { startColor, endColor } = gradientColors;
          if (!chartArea) {
            // Chart not yet rendered, return solid color as fallback
            return endColor;
          }
          const { left, right, top, bottom } = chartArea;
          const gradient = isHorizontalBar
            ? ctx.createLinearGradient(left, 0, right, 0)
            : ctx.createLinearGradient(0, bottom, 0, top);

          gradient.addColorStop(0, startColor);
          gradient.addColorStop(endPosition, endColor);
          if (endPosition < 1) {
            gradient.addColorStop(1, endColor);
          }
          return gradient;
        };
      });
    },
  };
}

/**
 * Creates a Chart.js plugin that applies gradient fills to line/area charts.
 * The gradient runs vertically from the line (top) to the baseline (bottom),
 * typically fading from the line color to transparent.
 */
export function createLineFillGradientPlugin(options: {
  bottomOpacity: number; // Opacity at the baseline
  topOpacity: number; // Opacity at the line
  bottomLuminosity: number; // Luminosity at the baseline
  topLuminosity: number; // Luminosity at the line
  gradientEndPosition: number; // How far up from baseline the gradient extends (0-1)
}) {
  const configTopOpacity = options.topOpacity;
  const bottomOpacity = options.bottomOpacity;
  const topLum = options.topLuminosity;
  const bottomLum = options.bottomLuminosity;
  const endPosition = options.gradientEndPosition;

  return {
    id: "lineFillGradient",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    beforeInit(chart: any) {
      if (chart.config.type !== "line") {
        return;
      }

      const topOpacity = configTopOpacity;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      chart.data.datasets.forEach((dataset: any) => {
        // Only apply to datasets with fill enabled
        if (dataset.fill === false || dataset.fill === undefined) {
          return;
        }

        // Skip if backgroundColor is already a function
        if (typeof dataset.backgroundColor === "function") {
          return;
        }

        // Get the base color from borderColor (line color) or backgroundColor
        const baseColor = dataset.borderColor || dataset.backgroundColor;
        const hexColor = extractHexColor(Array.isArray(baseColor) ? baseColor[0] : baseColor);
        if (!hexColor) {
          return;
        }

        // Create gradient colors using Color class
        const baseColorObj = Color.parse(hexColor);
        const topColor = baseColorObj.lighten(topLum).withAlpha(topOpacity).toRgbString();
        const bottomColor = baseColorObj.lighten(bottomLum).withAlpha(bottomOpacity).toRgbString();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dataset.backgroundColor = (context: any) => {
          const chartInstance = context.chart;
          const { ctx, chartArea } = chartInstance;
          if (!chartArea) {
            return topColor;
          }
          const { top, bottom } = chartArea;
          // Vertical gradient from top (line) to bottom (baseline)
          // endPosition: how far up from baseline the gradient extends
          // e.g., endPosition: 0.2 means bottom 20% has gradient, top 80% is solid topColor
          const gradient = ctx.createLinearGradient(0, top, 0, bottom);
          const transitionStart = 1 - endPosition;
          gradient.addColorStop(0, topColor);
          if (transitionStart > 0) {
            gradient.addColorStop(transitionStart, topColor);
          }
          gradient.addColorStop(1, bottomColor);
          return gradient;
        };
      });
    },
  };
}
